import { styled } from '@mui/material';
import type { ComponentProps, ReactNode, SyntheticEvent } from 'react';
import { useRef } from 'react';
import { useState } from 'react';

export interface ChkImageProps extends ComponentProps<typeof StyledImage> {
  errorComponent?: ReactNode;
  loadingComponent?: ReactNode;
  src: string;
}

export function ChkImage({
  errorComponent,
  src,
  alt,
  onError,
  onLoad,
  loadingComponent,
  sx,
  ...imageProps
}: ChkImageProps) {
  const [isError, setIsError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const cachedSrc = useRef('');

  // eslint-disable-next-line react-hooks/refs
  if (src !== cachedSrc.current) {
    // eslint-disable-next-line react-hooks/refs
    cachedSrc.current = src;

    if (
      src.startsWith('data:') ||
      src.startsWith('blob:') ||
      src.startsWith('/@fs/')
    ) {
      setIsLoaded(true);
      setIsError(false);
    } else if (src.trim() === '') {
      setIsLoaded(false);
      setIsError(true);
    } else {
      setIsLoaded(false);
      setIsError(false);
    }
  }

  const handleError = (event: SyntheticEvent<HTMLImageElement, Event>) => {
    if (event.currentTarget.src === cachedSrc.current) {
      setIsError(true);
      onError?.(event);
    }
  };

  const handleLoad = (event: SyntheticEvent<HTMLImageElement, Event>) => {
    if (event.currentTarget.src === cachedSrc.current) {
      setIsLoaded(true);
      onLoad?.(event);
    }
  };

  if (isError) {
    return errorComponent;
  }

  return (
    <>
      {!isLoaded && loadingComponent}
      <StyledImage
        sx={{ ...sx, ...(!isLoaded && { display: 'none' }) }}
        src={src}
        alt={alt}
        onError={handleError}
        onLoad={handleLoad}
        draggable={false}
        {...imageProps}
      />
    </>
  );
}

const StyledImage = styled('img')({});
